import { parseStringToIntOrDefault } from '@woolworthsnz/styleguide';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isoWeek from 'dayjs/plugin/isoWeek';
import { SiteDetail, TradingHours, TradingHourTime } from '../models';
import { dayOfWeekKeys } from './trading-hours.helper';

export enum TimeRequest {
	opening = 'opening',
	closing = 'closing',
	nextOpening = 'nextOpening',
}

type DayOfWeek = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';

function tradingHourOnDay(day: Date, tradingHourString?: string): Date {
	if (!tradingHourString) {
		return day;
	}

	const inDay = dayjs(day);
	const hoursString = tradingHourString.split(':')[0];
	const hours = hoursString === '00' ? 24 : parseStringToIntOrDefault(hoursString, 0);
	const minutes = parseStringToIntOrDefault(tradingHourString.split(':')[1], 0);
	return inDay.set('h', hours).set('m', minutes).toDate();
}

function isClosed(tomorrowTradingHours?: TradingHourTime): boolean {
	return tomorrowTradingHours?.startTime === '00:00:00' && tomorrowTradingHours?.endTime === '00:00:00';
}

function getNextOpeningHourFromDay(initialDay: dayjs.Dayjs, tradingHours: TradingHours): Date | undefined {
	dayjs.extend(isoWeek);
	for (const [index, dayOfWeek] of dayOfWeekKeys.entries()) {
		const currentDayOfWeek = initialDay.isoWeekday() - 1; // 0 - 6 starting on Monday

		// Skip days until we get to the initial day of the week
		if (currentDayOfWeek !== index) {
			continue;
		}

		const timesForDayOfWeek = tradingHours[dayOfWeek] as TradingHourTime;

		// if current day is not closed, return early
		if (!isClosed(timesForDayOfWeek)) {
			return tradingHourOnDay(initialDay.toDate(), timesForDayOfWeek?.startTime);
		}

		initialDay = initialDay.add(1, 'd'); // iterate to next day
	}

	return undefined;
}

function getNextOpeningHours(tradingHours: TradingHours[]): Date | undefined {
	const tomorrow = dayjs().add(1, 'd'); // start with tomorrow
	const tomorrowDayOfWeek = tomorrow.format('dddd').toLowerCase() as DayOfWeek;
	let nextOpening;

	// if tomorrow is Monday, using 2nd week
	if (tomorrowDayOfWeek === 'monday') {
		nextOpening = getNextOpeningHourFromDay(tomorrow, tradingHours[1]);
	} else {
		// get opening hours by the first week
		nextOpening = getNextOpeningHourFromDay(tomorrow, tradingHours[0]);

		// if there is no openning hour found, loop for the second week
		if (!nextOpening) {
			const dayDifferentToNextMonday = 7 - dayjs().isoWeekday() + 1;
			const nextMonday = dayjs().add(dayDifferentToNextMonday, 'd');
			nextOpening = getNextOpeningHourFromDay(nextMonday, tradingHours[1]);
		}
	}

	return nextOpening;
}

export function getTradingHourAsDate(tradingHours: TradingHours[], timeRequested: TimeRequest): Date | undefined {
	const currentDayOfWeek = dayjs().format('dddd').toLowerCase() as DayOfWeek;
	const today = dayjs().toDate();
	const todayTradingHours = tradingHours[0][currentDayOfWeek];

	switch (timeRequested) {
		case TimeRequest.opening:
			return tradingHourOnDay(today, todayTradingHours?.startTime);
		case TimeRequest.closing:
			return tradingHourOnDay(today, todayTradingHours?.endTime);
		case TimeRequest.nextOpening:
			return getNextOpeningHours(tradingHours);
		default:
			return tradingHourOnDay(today, todayTradingHours?.startTime);
	}
}

export const checkIfStoreOpened = (store?: SiteDetail): boolean => {
	dayjs.extend(isSameOrAfter);
	if (!store?.tradingHours) {
		return false;
	}

	const openingTime = getTradingHourAsDate(store?.tradingHours, TimeRequest.opening);
	const closingTime = getTradingHourAsDate(store?.tradingHours, TimeRequest.closing);
	const now = dayjs();

	if (openingTime === undefined || closingTime === undefined) {
		return false;
	}

	return now.isSameOrAfter(openingTime) && now.isBefore(closingTime);
};
